import Ionicons from "@expo/vector-icons/Ionicons";
import { useRouter } from "expo-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActivityIndicator,
  FlatList,
  Pressable,
  StyleSheet,
  View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

import { Avatar } from "@/components/ui/avatar";
import { ThemedText } from "@/components/themed-text";
import { Radius, Spacing } from "@/constants/theme";
import { useTheme } from "@/hooks/use-theme";
import { blockedSettingsStrings as strings } from "@/i18n/features/blocked-settings.i18n";
import { chatApi } from "@/api";
import { confirm } from "@/lib/dialog";
import type { BlockedUser } from "@/types";

/**
 * Every person this user has blocked, and the catch-all place to lift one.
 *
 * Not the primary route. When a conversation exists, Unblock sits on the thread
 * itself, next to where a dormant conversation offers "Ask to reopen" — that is
 * where somebody is already looking, and it carries the context of who the
 * person was.
 *
 * This exists because that route cannot cover everything: blocking is reachable
 * from declining a Cue, and no conversation is ever created in that case. Those
 * blocks would have nowhere to be lifted from at all, which is a permanent block
 * shipped by omission rather than by decision. So the thread is the natural
 * place, and this is the complete one.
 *
 * A `FlatList` rather than a `ScrollView`: this is user-generated and grows.
 */
export default function BlockedSettings() {
  const c = useTheme();
  const router = useRouter();
  const { t } = useTranslation();

  const [rows, setRows] = useState<BlockedUser[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    // Settled in callbacks rather than awaited in the effect body, and guarded
    // by `cancelled`. Awaiting here reads as a synchronous set to the hooks lint
    // and, more to the point, leaves a resolved fetch writing state into a
    // screen the user has already navigated away from.
    let cancelled = false;
    chatApi
      .blocked()
      .then((list) => {
        if (!cancelled) setRows(list);
      })
      .catch(() => {
        if (cancelled) return;
        // An empty list rather than a permanent spinner: the notice below says
        // the load failed, and a screen stuck loading says nothing at all.
        setRows([]);
        setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function unblock(target: BlockedUser) {
    // The app's own dialog, not `Alert.alert`: on web that is an empty function
    // and every confirmation routed through it is silently discarded, buttons
    // and callbacks alike. See `lib/dialog`.
    const agreed = await confirm({
      title: t(strings.confirmTitle, { name: target.displayName }),
      message: t(strings.confirmBody),
      confirmLabel: t(strings.unblock),
    });
    if (!agreed) return;

    setError(false);
    try {
      await chatApi.unblockUser(target.id);
      // Dropped locally rather than by refetching. The server refuses outright
      // when the block was not this caller's to lift, so reaching here means the
      // row is genuinely gone.
      setRows((current) =>
        (current ?? []).filter((row) => row.id !== target.id),
      );
    } catch {
      setError(true);
    }
  }

  return (
    <SafeAreaView style={[styles.safe, { backgroundColor: c.background }]}>
      <View style={[styles.header, { borderBottomColor: c.border }]}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t(strings.goBack)}
          onPress={() => router.back()}
          hitSlop={10}
          style={({ pressed }) => [styles.backButton, pressed && styles.pressed]}
        >
          <Ionicons name="chevron-back" size={23} color={c.tint} />
        </Pressable>

        <ThemedText variant="bodyStrong" style={styles.headerTitle}>
          {t(strings.title)}
        </ThemedText>

        <View style={styles.headerSpacer} />
      </View>

      {rows === null ? (
        <View style={styles.centre}>
          <ActivityIndicator color={c.tint} />
        </View>
      ) : (
        <FlatList
          data={rows}
          keyExtractor={(row) => row.id}
          contentContainerStyle={styles.content}
          ListEmptyComponent={
            <ThemedText variant="body" tone="muted" style={styles.empty}>
              {t(strings.empty)}
            </ThemedText>
          }
          renderItem={({ item }) => (
            <View
              style={[
                styles.row,
                { backgroundColor: c.surface, borderColor: c.border },
              ]}
            >
              <Avatar
                seed={item.avatarSeed}
                avatarConfig={item.avatarConfig}
                name={item.displayName}
                size={40}
              />

              <ThemedText variant="body" style={styles.name} numberOfLines={1}>
                {item.displayName}
              </ThemedText>

              <Pressable
                accessibilityRole="button"
                accessibilityLabel={t(strings.unblock)}
                onPress={() => void unblock(item)}
                style={({ pressed }) => [
                  styles.unblock,
                  { borderColor: c.border },
                  pressed && styles.pressed,
                ]}
              >
                <ThemedText variant="caption" style={{ color: c.tint }}>
                  {t(strings.unblock)}
                </ThemedText>
              </Pressable>
            </View>
          )}
        />
      )}

      {error ? (
        <ThemedText variant="caption" tone="muted" style={styles.error}>
          {t(strings.failed)}
        </ThemedText>
      ) : null}

    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1 },
  header: {
    minHeight: 56,
    flexDirection: "row",
    alignItems: "center",
    borderBottomWidth: StyleSheet.hairlineWidth,
    paddingHorizontal: Spacing.sm,
  },
  backButton: { padding: Spacing.xs },
  headerTitle: { flex: 1, textAlign: "center" },
  headerSpacer: { width: 31 },
  centre: { flex: 1, alignItems: "center", justifyContent: "center" },
  // The same column the inbox uses, not the full width of the window. This is
  // a list of people with a button on each row — the same shape the inbox
  // draws — and stretched edge to edge on a wide screen a 40px avatar sat a
  // thousand points away from its own Unblock button.
  content: {
    width: "100%",
    maxWidth: 760,
    alignSelf: "center",
    padding: Spacing.md,
    gap: Spacing.sm,
  },
  empty: { textAlign: "center", marginTop: Spacing.lg },
  row: {
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.sm,
    padding: Spacing.sm,
    borderRadius: Radius.md,
    borderWidth: StyleSheet.hairlineWidth,
  },
  name: { flex: 1 },
  unblock: {
    paddingHorizontal: Spacing.sm,
    paddingVertical: Spacing.xs,
    borderRadius: Radius.sm,
    borderWidth: StyleSheet.hairlineWidth,
  },
  error: { textAlign: "center", paddingBottom: Spacing.md },
  pressed: { opacity: 0.6 },
});
